import { useEffect, useState } from "react";
import { supabase } from "../lib/supabaseClient";

const ADMIN_USER_IDS = [
  "39354205-3d56-466b-8aae-d90a069e5f77",
];

export const PLAN_LIMITS = {
  free:   { qrCodes: 5,  dynamic: false, leads: false, feedback: false, analytics: false, bulk: false, wifi: false, whatsapp: false, upi: false, menu: false, vcard: false },
  trial:  { qrCodes: 50, dynamic: true,  leads: true,  feedback: true,  analytics: true,  bulk: true,  wifi: true,  whatsapp: true,  upi: true,  menu: true,  vcard: true  },
  pro:    { qrCodes: 50, dynamic: true,  leads: true,  feedback: true,  analytics: true,  bulk: false, wifi: true,  whatsapp: true,  upi: true,  menu: true,  vcard: true  },
  agency: { qrCodes: Infinity, dynamic: true, leads: true, feedback: true, analytics: true, bulk: true, wifi: true, whatsapp: true, upi: true, menu: true, vcard: true },
};

export const TRIAL_DAYS = 7;

export default function usePlan(userId, isAdmin = false) {
  const [plan, setPlan] = useState("agency");
  const [limits, setLimits] = useState(PLAN_LIMITS.agency);
  const [loading, setLoading] = useState(true);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [isOnTrial, setIsOnTrial] = useState(false);

  useEffect(() => {
    if (!userId) {
      setLoading(false);
      return;
    }

    // Admin always gets Agency plan free — no DB check needed
    if (isAdmin || ADMIN_USER_IDS.includes(userId)) {
      setPlan("agency");
      setLimits(PLAN_LIMITS.agency);
      setTrialDaysLeft(null);
      setIsOnTrial(false);
      setLoading(false);
      return;
    }

    async function fetchPlan() {
      setLoading(true);
      try {
        // 1. Check active subscription — get most recent active one
        // Using limit(1) instead of maybeSingle() to avoid error when multiple rows exist
        const { data: subs } = await supabase
          .from("subscriptions")
          .select("plan, status, expires_at")
          .eq("user_id", userId)
          .eq("status", "active")
          .order("created_at", { ascending: false })
          .limit(1);

        const sub = subs?.[0] || null;

        if (sub) {
          // Check if subscription is still valid (not expired)
          const notExpired = !sub.expires_at || new Date(sub.expires_at) > new Date();
          if (notExpired) {
            setPlan(sub.plan);
            setLimits(PLAN_LIMITS[sub.plan] || PLAN_LIMITS.free);
            setIsOnTrial(false);
            setTrialDaysLeft(null);
            setLoading(false);
            return;
          }
          // Subscription exists but expired — fall through to trial check
        }

        // 2. Check 7-day free trial based on account creation date
        const { data: { user } } = await supabase.auth.getUser();
        const accountCreatedAt = user?.created_at ? new Date(user.created_at) : null;

        if (accountCreatedAt) {
          const now = new Date();
          const daysSinceSignup = Math.floor((now - accountCreatedAt) / (1000 * 60 * 60 * 24));
          const daysLeft = TRIAL_DAYS - daysSinceSignup;

          if (daysLeft > 0) {
            setPlan("trial");
            setLimits(PLAN_LIMITS.trial);
            setIsOnTrial(true);
            setTrialDaysLeft(daysLeft);
            setLoading(false);
            return;
          }
        }

        // 3. Trial expired, no active subscription — free plan
        setPlan("free");
        setLimits(PLAN_LIMITS.free);
        setIsOnTrial(false);
        setTrialDaysLeft(0);

      } catch (err) {
        console.error("usePlan error:", err);
        // On error, keep agency (the optimistic default) to avoid false paywalls
        setPlan("agency");
        setLimits(PLAN_LIMITS.agency);
      } finally {
        setLoading(false);
      }
    }

    fetchPlan();
  }, [userId, isAdmin]);

  return { plan, limits, loading, trialDaysLeft, isOnTrial };
}
